import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import UserContext from "../../context/user";
import CartContext from "../../context/cart";
import Icon from "../icon";
import useCartRequestUpdateMutation from "../../hooks/mutations/useCartRequestUpdateMutation";
import ChoicePoints from "../choice-points";

const PickUpPoints = ({ tempCartToSubmit, setTempCartToSubmit, setTempBookingDate }) => {
  const { logged, callApi } = useContext(UserContext);
  const {
    labels,
    survey,
    handlePrevious,
    formData,
    cartRequest,
    partial,
    campaign,
    handleNext,
    setFormData,
  } = useContext(CartContext);
  const [selected, setSelected] = useState();
  const [validationError, setValidationError] = useState();

  const pickUpPointQuestionId = useMemo(
    () =>
      survey?.definition?.sections?.[4]?.questions?.find((q) => q.questionType === "Number")?.id,
    [survey]
  );

  useEffect(() => {
    if (formData?.[pickUpPointQuestionId]) {
      setSelected(formData[pickUpPointQuestionId]);
    }
  }, [pickUpPointQuestionId, formData]);

  const {
    isFetching,
    error,
    data: pickUpPoints,
  } = useQuery(["pick-up-points"], () => callApi(`/cart/pickup-point/search?limit=100&offset=0`), {
    enabled: logged,
    select: (data) => data?.list,
  });

  const { mutateAsync: updateCartRequest } = useCartRequestUpdateMutation();

  const updateCartRequestFromValues = useCallback(async () => {
    if (pickUpPointQuestionId) {
      const doClearReservation = formData[pickUpPointQuestionId]
        ? Number(selected) !== Number(formData[pickUpPointQuestionId])
        : true;
      //Clear reservation answer
      const clearFormData = formData;
      if (doClearReservation) {
        const reservationQuestionId = survey?.definition?.sections?.[4]?.questions?.find(
          (q) => q.questionType === "Reservation"
        );
        delete clearFormData?.[reservationQuestionId];
      }

      // salva i parametri del nuovo booking in uno stato di appoggio
      // occorre modificare SOLO questi parametri del contesto, perché sono quelli che vengono poi letti dalla mutation quando viene fatta la chiamata API.
      // la mutation aggiorna anche il cart request ad ogni step, ma non ha senso farlo qui
      if (tempCartToSubmit || cartRequest.status === "BOOKED") {
        const newValues = {
          ...clearFormData,
          ...(tempCartToSubmit?.values ?? {}),
          [pickUpPointQuestionId]: Number(selected),
        };
        handleNext();
        setTempBookingDate({
          pickUpPointId: selected,
        });
        setFormData(newValues);
        return;
      }

      await updateCartRequest({
        campaignId: campaign?.id,
        cartRequest,
        survey,
        formData: clearFormData,
        clearReservation: doClearReservation,
        values: { [pickUpPointQuestionId]: Number(selected) },
        email: cartRequest?.welfareEmail,
        phone: cartRequest?.welfarePhone,
        partial,
      });
    }
  }, [
    pickUpPointQuestionId,
    formData,
    selected,
    tempCartToSubmit,
    cartRequest,
    updateCartRequest,
    campaign?.id,
    survey,
    partial,
    handleNext,
    setTempBookingDate,
    setFormData,
  ]);

  const handleSubmit = useCallback(async () => {
    if (selected) {
      updateCartRequestFromValues();
    } else {
      setValidationError(true);
    }
  }, [selected, updateCartRequestFromValues]);

  return error ? (
    <div className="error">Error retrieving pickup points</div>
  ) : (
    <>
      {isFetching ? (
        <div className="loading-spinner" />
      ) : (
        <>
          <ChoicePoints
            labels={labels}
            pickUpPoints={pickUpPoints}
            selected={selected}
            setSelected={setSelected}
          />
          <div className="form__buttons-wrapper">
            {validationError && (
              <div className="error text-center">{labels?.THIS_FIELD_IS_MANDATORY}</div>
            )}
            <div className="form__buttons form__buttons--center">
              <button className="link" onClick={handlePrevious} type="button">
                <Icon name="angle-down" />
                {labels?.WELFARE_FORM_BTN_PREVIOUS || "Previous"}
              </button>
              <button className="button primary form__submit" onClick={handleSubmit}>
                {labels?.WELFARE_FORM_BTN_NEXT || "Next"}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PickUpPoints;
